import type { FinancialIndicatorsConfig } from './financialIndicators.types';

export interface AppConfig {
  isShowDualBalanceEnabled?: boolean;
  isInstrumentationEnabled?: boolean;
  isMultipleApprovalInCommitteeEnabled?: boolean;
  isClientEnabled?: boolean;
  isGuaranteesEnabled?: boolean;
  isQuotationsEnabled?: boolean;
  /** Habilita USD en originación/comité/riesgo cuando isQuotationsEnabled es false (flag global por cliente). */
  flagUsdCurrencyEnabled?: boolean;
  /** Grupo económico en datos del cliente (origination) */
  isEconomicGroupEnabled?: boolean;
  isCasfogEnabled?: boolean;
  isClientCategoryTypeEnabled?: boolean;
  isRiskStep4IncomeStatementV2Enabled?: boolean;
  isApplicationEffectiveDateEnabled?: boolean;
  isApplicationCreditTypeEnabled?: boolean;
  /** Calculador crédito máximo (49625); también define layout Paso 4 con Solicitud después de la info financiera */
  isMaxCreditCalculatorEnabled?: boolean;
  /** Tabla de aportes patronales (Nosis AP_12m) en resumen riesgo / central deudores */
  isRiskEmployerContributionsDetailEnabled?: boolean;
  /** Dropdown Gerencia en originación paso 1 + notificaciones por paso de comité */
  isManagementSelectionEnabled?: boolean;
  /**
   * Task 50111 (cliente Innova): habilita el flujo de comité por quórum de usuarios
   * COMITE en lugar del flujo legacy por roles secuenciales. Es mutuamente exclusivo
   * con `isMultipleApprovalInCommitteeEnabled` (validado en backend); si por alguna
   * razón ambos llegaran true, el front debe priorizar este nuevo flag.
   */
  isCommitteeMemberQuorumEnabled?: boolean;
  /** Distribución de montos aprobados entre varias SGR al cerrar comité (legacy multi-step). */
  isMultiSgrApprovalEnabled?: boolean;
  /** Patrimonio neto detallado (9 campos) vs simplificado (Capital + Otros). Default false = simplificado. */
  isDetailedNetWorthEnabled?: boolean;
  /** Cantidad mínima de aprobaciones COMITE para habilitar el envío manual a INS. */
  committeeMinManualQuorum?: number;
  /** Cantidad de aprobaciones COMITE que disparan automáticamente el pase a INS. */
  committeeAutoQuorum?: number;
  liquidationsMaxRangeYears?: number;
  /** Sección "Datos Complementarios" (Compras, etc.) en pasos 2 y 4 de riesgo */
  isComplementaryDataEnabled?: boolean;
  /**
   * Habilita el aprobador masivo (bulk) en el listado de comité (cliente
   * Cementos LEGACY MVP). Cuando es `true`, la página `/comite` muestra
   * checkbox por fila (solo si `committee_can_approve === true` viene del
   * BFF) y barra superior con botones "Aprobar" / "Declinar o solicitar
   * cambios" para procesar hasta 10 carpetas en una sola operación.
   * Origen: `application.configuration.is_committee_bulk_approval_enabled`
   * (ms-application V74) → `movil-security` lo expone en
   * `ConfigurationResponse` del login.
   */
  isCommitteeBulkApprovalEnabled?: boolean;
  /** Origination step 2: "/firmada por contador" suffix on document labels 005 and 010. */
  isOriginationAccountantSignatureLabelsEnabled?: boolean;
  /** Paso 2 originación: carga opcional del balance del ejercicio anterior (Innova). */
  isOriginationPreviousBalanceEnabled?: boolean;
  /** Dashboard inicial con conteo de carpetas por estado (6 tarjetas Innova). */
  isDashboardStatusCountEnabled?: boolean;
  /** Modal Datos de la operación en paso 4 originación al aceptar documentación. */
  isOriginationDataModalEnabled?: boolean;
  /**
   * Operaciones Públicas sindicadas: checkbox en paso 3 + cierre a FIN desde Summary.
   * Origen: `application.configuration.is_syndicated_public_operations_enabled`.
   */
  isSyndicatedPublicOperationsEnabled?: boolean;
  country?: 'ARGENTINA' | 'URUGUAY';
  isUruguayEnabled?: boolean;
  /**
   * Strict Argentina allowlist for NOSIS/CASFOG/BCRA. Derived from raw `country`
   * before normalize fallback — unknown/third-country stays false.
   */
  isArgentinaEnabled?: boolean;
  localCurrencyCode?: 'ARS' | 'UYU';
  financialIndicators?: FinancialIndicatorsConfig;
}

export const CONFIG_DEFAULTS: Required<
  Omit<AppConfig, 'financialIndicators' | 'isArgentinaEnabled'>
> = {
  isShowDualBalanceEnabled: false,
  isInstrumentationEnabled: true,
  isMultipleApprovalInCommitteeEnabled: false,
  isClientEnabled: false,
  isGuaranteesEnabled: false,
  isQuotationsEnabled: false,
  flagUsdCurrencyEnabled: true,
  isEconomicGroupEnabled: false,
  isCasfogEnabled: true,
  isClientCategoryTypeEnabled: false,
  isRiskStep4IncomeStatementV2Enabled: false,
  isApplicationEffectiveDateEnabled: false,
  isApplicationCreditTypeEnabled: false,
  isMaxCreditCalculatorEnabled: false,
  isRiskEmployerContributionsDetailEnabled: false,
  isManagementSelectionEnabled: false,
  isCommitteeMemberQuorumEnabled: false,
  isMultiSgrApprovalEnabled: false,
  committeeMinManualQuorum: 2,
  committeeAutoQuorum: 3,
  liquidationsMaxRangeYears: 20,
  isDetailedNetWorthEnabled: false,
  isComplementaryDataEnabled: true,
  isCommitteeBulkApprovalEnabled: false,
  isOriginationAccountantSignatureLabelsEnabled: false,
  isOriginationPreviousBalanceEnabled: false,
  isDashboardStatusCountEnabled: false,
  isOriginationDataModalEnabled: false,
  isSyndicatedPublicOperationsEnabled: false,
  country: 'ARGENTINA',
  isUruguayEnabled: false,
  localCurrencyCode: 'ARS',
};
